import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import type { Clip, VfxShot, VfxShotClip, VfxShotClipRole } from '@/types/database'
import { createVfxShotClipLink } from '@/lib/api/vfxShotClips'

interface LinkClipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vfxShot: VfxShot
  availableClips: Clip[]
  existingLinks: VfxShotClip[]
  onSuccess: () => void
}

const RELATIONSHIP_TYPES: { value: VfxShotClipRole; label: string; description: string }[] = [
  { value: 'source', label: 'Source', description: 'Original source clip from timeline' },
  { value: 'plate', label: 'Plate', description: 'Background plate for VFX composition' },
  { value: 'comp', label: 'Comp', description: 'Composite/comp clip' },
  { value: 'matte', label: 'Matte', description: 'Matte clip' },
  { value: 'clean_plate', label: 'Clean Plate', description: 'Clean plate without actors/objects' },
  { value: 'reference', label: 'Reference', description: 'Reference footage' },
  { value: 'final', label: 'Final', description: 'Final rendered output' },
  { value: 'render', label: 'Render', description: 'VFX render' },
]

export function LinkClipDialog({
  open,
  onOpenChange,
  vfxShot,
  availableClips,
  existingLinks,
  onSuccess
}: LinkClipDialogProps) {
  const [selectedClipId, setSelectedClipId] = useState<string>('')
  const [relationshipType, setRelationshipType] = useState<VfxShotClipRole>('source')
  const [clipFrameStart, setClipFrameStart] = useState<string>('')
  const [clipFrameEnd, setClipFrameEnd] = useState<string>('')
  const [trackNumber, setTrackNumber] = useState<string>('')
  const [trackName, setTrackName] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedClipId) {
      toast.error('Please select a clip')
      return
    }

    // Check if this exact relationship already exists
    const alreadyLinked = existingLinks.some(
      link => link.clip_id === selectedClipId && link.relationship_type === relationshipType
    )

    if (alreadyLinked) {
      toast.error(`This clip is already linked as a ${relationshipType} clip`)
      return
    }

    try {
      setLoading(true)

      await createVfxShotClipLink({
        vfx_shot_id: vfxShot.id,
        clip_id: selectedClipId,
        relationship_type: relationshipType,
        clip_frame_start: clipFrameStart ? parseInt(clipFrameStart) : undefined,
        clip_frame_end: clipFrameEnd ? parseInt(clipFrameEnd) : undefined,
        track_number: trackNumber ? parseInt(trackNumber) : undefined,
        track_name: trackName || undefined,
        notes: notes || undefined,
      })

      const selectedClip = availableClips.find(c => c.id === selectedClipId)
      toast.success(`Linked "${selectedClip?.name}" as ${relationshipType} clip`)

      // Reset form
      setSelectedClipId('')
      setRelationshipType('source')
      setClipFrameStart('')
      setClipFrameEnd('')
      setTrackNumber('')
      setTrackName('')
      setNotes('')

      onSuccess()
    } catch (error: any) {
      console.error('Failed to link clip:', error)
      toast.error(error.message || 'Failed to link clip')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link Clip to VFX Shot</DialogTitle>
          <DialogDescription>
            Add a clip relationship to "{vfxShot.shot_name}". You can link multiple clips with different relationship types.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Clip Selection */}
          <div className="space-y-2">
            <Label htmlFor="clip">Clip *</Label>
            <Select value={selectedClipId} onValueChange={setSelectedClipId}>
              <SelectTrigger id="clip">
                <SelectValue placeholder="Select a clip..." />
              </SelectTrigger>
              <SelectContent>
                {availableClips.map((clip) => (
                  <SelectItem key={clip.id} value={clip.id}>
                    {clip.name}
                    {clip.tape_name && ` (${clip.tape_name})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Relationship Type */}
          <div className="space-y-2">
            <Label htmlFor="relationship-type">Relationship Type *</Label>
            <Select
              value={relationshipType}
              onValueChange={(value) => setRelationshipType(value as VfxShotClipRole)}
            >
              <SelectTrigger id="relationship-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{type.label}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Frame Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frame-start">Frame Start</Label>
              <Input
                id="frame-start"
                type="number"
                placeholder="e.g., 1000"
                value={clipFrameStart}
                onChange={(e) => setClipFrameStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="frame-end">Frame End</Label>
              <Input
                id="frame-end"
                type="number"
                placeholder="e.g., 1150"
                value={clipFrameEnd}
                onChange={(e) => setClipFrameEnd(e.target.value)}
              />
            </div>
          </div>

          {/* Track Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="track-number">Track Number</Label>
              <Input
                id="track-number"
                type="number"
                placeholder="e.g., 1"
                value={trackNumber}
                onChange={(e) => setTrackNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="track-name">Track Name</Label>
              <Input
                id="track-name"
                type="text"
                placeholder="e.g., V1 - Plates"
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this relationship..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedClipId}>
              {loading ? 'Linking...' : 'Link Clip'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
